/**
 * DOCUMENTATION:
 * Component: GCPResourceHierarchy
 * Purpose: Deep dive into Org Nodes, Folders, Projects, and IAM.
 * * NOTE FOR JUNIOR DEVS:
 * - Always use 'Alert' for exam-specific tips; it makes them scannable.
 * - Resource Hierarchy is the 'Skeleton' of GCP. Permissions flow DOWN.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, // ✅ Added missing import
    Divider
} from "@mui/material";

/* Icons */
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SecurityIcon from '@mui/icons-material/Security';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d", // GCP Deep Space Blue
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.1)",
    color: "white",
}));

const HierarchyItem = styled(Box)(({ theme, level }) => ({
    marginLeft: theme.spacing(level * 4),
    padding: theme.spacing(1.5),
    borderLeft: "2px solid #4285F4",
    marginBottom: theme.spacing(1),
    backgroundColor: "rgba(255, 255, 255, 0.02)",
    borderRadius: "0 8px 8px 0",
}));

export default function GCPResourceHierarchy() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h3"} fontWeight={900} color="#4285F4">
                    HIERARCHY & IAM
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>The Foundation of Cloud Security</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                {/* COLUMN 1: The Hierarchy Tree */}
                <Grid item xs={12} md={7}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <AccountTreeIcon sx={{ color: "#4285F4" }} />
                            <Typography variant="h5" fontWeight={800}>GCP Resource Tree</Typography>
                        </Stack>



                        <HierarchyItem level={0}>
                            <Typography variant="subtitle2" color="#EA4335">Organization Node</Typography>
                            <Typography variant="caption">The root node representing your entire company.</Typography>
                        </HierarchyItem>
                        <HierarchyItem level={1}>
                            <Typography variant="subtitle2" color="#FBBC04">Folders</Typography>
                            <Typography variant="caption">Used to group projects by department or environment (Dev/Prod).</Typography>
                        </HierarchyItem>
                        <HierarchyItem level={2}>
                            <Typography variant="subtitle2" color="#34A853">Projects</Typography>
                            <Typography variant="caption">The container for all your resources. API & Billing are enabled here.</Typography>
                        </HierarchyItem>

                        <Alert severity="info" sx={{ mt: 3, bgcolor: "rgba(66, 133, 244, 0.1)", color: "white", border: '1px solid #4285F4' }}>
                            <strong>PCA EXAM TIP:</strong> Access is inherited. If a user is a 'Viewer' at the Project level, they cannot be 'denied' that role on a specific VM inside it.
                        </Alert>
                    </SectionCard>
                </Grid>

                {/* COLUMN 2: IAM Identity */}
                <Grid item xs={12} md={5}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SecurityIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>Identity Management</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                            GCP IAM follows the policy of **Least Privilege**. Only give users the bare minimum permissions.
                        </Typography>

                        <Stack spacing={2}>
                            <Box sx={{ p: 2, border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#4285F4">Google Accounts</Typography>
                                <Typography variant="caption">Individual users or G-Suite identities.</Typography>
                            </Box>
                            <Box sx={{ p: 2, border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#4285F4">Service Accounts</Typography>
                                <Typography variant="caption">Used by applications (VMs, Cloud Run) to call other APIs.</Typography>
                            </Box>
                            <Box sx={{ p: 2, border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#4285F4">Cloud Identity Groups</Typography>
                                <Typography variant="caption">Best practice: Assign roles to groups, not individuals.</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
